import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';

import NavBar from './NavBar';
import Footer from './Footer';
import ScrollProgress from '../../components/ScrollProgress';

import Hero from './sections/Hero';
import TrustStrip from './sections/TrustStrip';
import TwoWayValue from './sections/TwoWayValue';
import HowItWorks from './sections/HowItWorks';
import Sectors, { SECTOR_HASH_ALIASES } from './sections/Sectors';
import NorwayCoverage from './sections/NorwayCoverage';
import Testimonial from './sections/Testimonial';
import Stories from './sections/Stories';
import AboutNorway from './sections/AboutNorway';
import FAQ from './sections/FAQ';
import FinalCTA from './sections/FinalCTA';

/**
 * EditorialLanding — Facelift Fase 2 (Feb 2026)
 *
 * Editorial Scandinavian rebuild of `/`. Aesop.com × Linear.app × norsk
 * designtradisjon. 8 seksjoner + global navbar/footer fra Fase 1.
 *
 * Section order (LÅST):
 *   1. Hero              — typography-only, subtile fjord SVG-linje
 *   2. TrustStrip        — GDPR/Helsedir/DSB/NAV editorial wordmarks
 *   3. TwoWayValue       — For fagarbeidere ↔ For kommuner split
 *   4. HowItWorks        — 4 nummererte steg
 *   5. Sectors           — 3 kort med line-art ikoner
 *   6. Testimonial       — bg-ink inverted (magazine-spread tempo)
 *   7. Stories           — 3 curated fra config/stories.js (bg-paper)
 *   8. AboutNorway       — Kristiansand-teaser til /om-norge
 *   9. FAQ               — custom accordion på bg-paper-cool
 *  10. FinalCTA          — sentrert closer
 */
export default function EditorialLanding() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { language, changeLanguage } = useLanguage();

  useEffect(() => {
    if (user) navigate('/dashboard');
  }, [user, navigate]);

  // Hash-scroll (f.eks. /#sektorer eller dybdelenke /#sektorer-helse) — retry til seksjonen er montert
  useEffect(() => {
    if (!location.hash) return;
    const raw = location.hash.slice(1);
    const id = SECTOR_HASH_ALIASES[raw] || raw;
    let tries = 0;
    let timer = null;
    const attempt = () => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      else if (tries++ < 10) timer = setTimeout(attempt, 100);
    };
    timer = setTimeout(attempt, 50);
    return () => clearTimeout(timer);
  }, [location.hash]);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="font-sans antialiased bg-paper text-ink">
      <NavBar
        onNavigate={navigate}
        scrollToSection={scrollToSection}
        language={language}
        changeLanguage={changeLanguage}
      />
      <ScrollProgress />

      <main>
        <Hero
          onPrimary={() => navigate('/signup?role=professional')}
          onSecondary={() => navigate('/for-kommuner')}
        />
        <TrustStrip />
        <TwoWayValue
          onProCta={() => navigate('/signup?role=professional')}
          onEmpCta={() => navigate('/signup?role=employer')}
        />
        <HowItWorks />
        <Sectors onNavigate={navigate} />
        <NorwayCoverage />
        <Testimonial />
        <Stories onNavigate={navigate} />
        <AboutNorway onNavigate={navigate} />
        <FAQ />
        <FinalCTA
          onPrimary={() => navigate('/signup')}
          onLogin={() => navigate('/login')}
        />
      </main>

      <Footer />
    </div>
  );
}
